import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "../../services/apiCalls";

const NotificationPreferencesModal = ({ open, onClose }) => {
  const [preferences, setPreferences] = useState({
    risk_enabled: true,
    recommendations_enabled: true,
    questionnaires_enabled: true,
    administrative_enabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation("notifications");

  useEffect(() => {
    if (open) {
      fetchPreferences();
    }
  }, [open]);

  const fetchPreferences = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getNotificationPreferences();
      if (data) {
        setPreferences({
          risk_enabled: data.risk_enabled,
          recommendations_enabled: data.recommendations_enabled,
          questionnaires_enabled: data.questionnaires_enabled,
          administrative_enabled: data.administrative_enabled,
        });
      }
    } catch (err) {
      setError(t("preferences.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key) => (event) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: event.target.checked,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateNotificationPreferences(preferences);
      onClose();
    } catch (err) {
      setError(t("preferences.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("preferences.title")}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("preferences.description")}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.risk_enabled}
                    onChange={handleChange("risk_enabled")}
                    color="error"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t("preferences.categories.risk")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("preferences.descriptions.risk")}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.recommendations_enabled}
                    onChange={handleChange("recommendations_enabled")}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t("preferences.categories.recommendations")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("preferences.descriptions.recommendations")}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.questionnaires_enabled}
                    onChange={handleChange("questionnaires_enabled")}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t("preferences.categories.questionnaires")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("preferences.descriptions.questionnaires")}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.administrative_enabled}
                    onChange={handleChange("administrative_enabled")}
                    color="secondary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t("preferences.categories.administrative")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("preferences.descriptions.administrative")}
                    </Typography>
                  </Box>
                }
              />
            </FormGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("preferences.cancel")}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={20} /> : t("preferences.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPreferencesModal;
